import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, Form, Input, InputNumber, Select, Space } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { FormPage } from "../../components/Common";
import SearchSelect from "../../components/SearchSelect";
import { useMutations } from "../../hooks/useApiMutation";

const { TextArea } = Input;

const PAYMENT_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Paid", label: "Paid" },
  { value: "Failed", label: "Failed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Refunded", label: "Refunded" },
  { value: "Chargeback", label: "Chargeback" },
];

/**
 * Full-page create/edit form for payments. Amounts are derived from the selected
 * subscription's package (top-up price, monthly/annual periods), mirroring the
 * server-side billing rules.
 *
 * Top-ups are sold as credit, not as an allowance increase: the tokens sit in a wallet,
 * are spent only after the cycle allowance runs out, and do not come back next month.
 * The form says so out loud, because "add tokens" reads like the opposite.
 */
const PaymentFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = location.state?.record ?? null;
  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionMeta, setSubscriptionMeta] = useState(null);
  const { mutate } = useMutations();

  const paymentType = Form.useWatch("paymentType", form) || "Regular";
  const currentStatus = Form.useWatch("status", form) || "Pending";
  const currencyCode = Form.useWatch("currencyCode", form) || "EUR";
  const units = Form.useWatch("units", form) || 1;

  // Generated once per create, sent with the payload: a double-clicked submit or a retried
  // request returns the original payment instead of charging the client twice.
  const idempotencyKey = useMemo(
    () =>
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `topup-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    []
  );

  const pack = subscriptionMeta?.package;
  const topUpPreview = useMemo(() => {
    if (paymentType !== "TopUp" || !pack) return null;

    const packTokens = Number(pack.topUpTokenCount || 0);
    const packPrice = Number(pack.topUpPrice || 0);
    if (packTokens <= 0 || packPrice <= 0) return { misconfigured: true };

    const net = packPrice * units;
    const vat = (net * Number(pack.vatRate || 0)) / 100;
    return {
      misconfigured: false,
      tokens: packTokens * units,
      net,
      vat,
      gross: net + vat,
    };
  }, [paymentType, pack, units]);

  const fetchSubscription = useCallback(
    async (subId) => {
      if (!subId) {
        setSubscriptionMeta(null);
        return null;
      }
      let subscription = null;
      await mutate("getSubscription", {
        method: "get",
        endpoint: `/subscription/${subId}`,
        onSuccess: (res) => {
          subscription = res || null;
          setSubscriptionMeta(res || null);
        },
      });
      return subscription;
    },
    [mutate]
  );

  const recalculateAmounts = useCallback(
    (meta, values) => {
      if (!meta?.package) return;

      const fromDate = values.fromDate || dayjs();
      let amount = meta.package.totalPayment || 0;
      let toDate = values.toDate || null;

      if ((values.paymentType || "Regular") === "TopUp") {
        // No fallback to the base package price: the server refuses a top-up on a package with
        // no pack configured, and showing a plausible amount here would only hide that.
        amount = (meta.package.topUpPrice || 0) * (values.units || 1);
        toDate = null;
      } else if (meta.package.periodType === "Annual") {
        toDate = fromDate.add(1, "year");
      } else {
        const effectiveTo = toDate && toDate.isAfter(fromDate) ? toDate : fromDate.add(1, "month");
        const months =
          (effectiveTo.year() - fromDate.year()) * 12 +
          (effectiveTo.month() - fromDate.month());
        amount = (meta.package.totalPayment || 0) * Math.max(months, 1);
        toDate = effectiveTo;
      }

      form.setFieldsValue({
        amount,
        currencyCode: meta.package.currencyCode,
        toDate,
      });
    },
    [form]
  );

  useEffect(() => {
    if (isEdit && !editing) {
      navigate("/payments", { replace: true });
      return;
    }

    if (editing) {
      fetchSubscription(editing.subscriptionId);
      form.setFieldsValue({
        subscriptionId: editing.subscriptionId,
        paymentType: editing.paymentType,
        status: editing.status,
        fromDate: editing.fromDate ? dayjs(editing.fromDate) : null,
        toDate: editing.toDate ? dayjs(editing.toDate) : null,
        amount: editing.amount,
        currencyCode: editing.currencyCode,
        externalProvider: editing.externalProvider,
        externalReference: editing.externalReference,
        failureReason: editing.failureReason,
        notes: editing.notes,
        collectedAtUtc: editing.collectedAtUtc ? dayjs(editing.collectedAtUtc) : null,
      });
    } else {
      form.setFieldsValue({
        subscriptionId: null,
        paymentType: "Regular",
        status: "Paid",
        fromDate: dayjs(),
        toDate: null,
        amount: 0,
        units: 1,
        currencyCode: "EUR",
        collectedAtUtc: dayjs(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      subscriptionId: isEdit ? editing.subscriptionId : values.subscriptionId,
      paymentType: values.paymentType,
      status: values.status,
      // Datat e periudhës s'ndryshojnë pas krijimit (fushat janë të bllokuara te editimi),
      // ndaj te editimi NUK dërgohen: serveri i kthen pa zonë orare ("2026-08-31T00:00:00"),
      // dayjs i lexon si orë lokale dhe toISOString() i zhvendos (30.08 22:00Z) — serveri
      // e refuzonte çdo ruajtje me "Payment dates cannot be changed after creation"
      // (pagesa 10 e SHELL-it, 26.09.2026).
      fromDate:
        !isEdit && values.paymentType === "Regular" && values.fromDate
          ? values.fromDate.toISOString()
          : undefined,
      toDate:
        !isEdit && values.paymentType === "Regular" && values.toDate
          ? values.toDate.toISOString()
          : undefined,
      units: values.paymentType === "TopUp" ? values.units || 1 : undefined,
      idempotencyKey: isEdit ? undefined : idempotencyKey,
      externalProvider: values.externalProvider,
      externalReference: values.externalReference,
      failureReason: values.failureReason,
      notes: values.notes,
      collectedAtUtc: values.collectedAtUtc
        ? values.collectedAtUtc.toISOString()
        : undefined,
    };

    setSubmitting(true);
    await mutate(isEdit ? "editPayment" : "createPayment", {
      method: isEdit ? "put" : "post",
      endpoint: isEdit ? `/payments/${id}` : "/payments",
      payload,
      onSuccess: () => navigate("/payments"),
    });
    setSubmitting(false);
  };

  return (
    <FormPage
      title={isEdit ? "Update Payment" : "Create Payment"}
      subtitle={
        isEdit
          ? `${editing?.clientName ?? ""} — ${editing?.packageName ?? ""}`.trim()
          : "Record a payment against a subscription. Amounts follow the package pricing."
      }
      backTo="/payments"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitText={isEdit ? "Save Changes" : "Create Payment"}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={async (changed, values) => {
          if (changed.subscriptionId && !isEdit) {
            const meta = await fetchSubscription(changed.subscriptionId);
            recalculateAmounts(meta, values);
          }

          if (
            !isEdit &&
            (changed.paymentType || changed.fromDate || changed.toDate || changed.units)
          ) {
            recalculateAmounts(subscriptionMeta, values);
          }

          if (changed.status) {
            if (changed.status === "Paid" && !values.collectedAtUtc) {
              form.setFieldsValue({ collectedAtUtc: dayjs() });
            }
            if (changed.status !== "Failed") {
              form.setFieldsValue({ failureReason: undefined });
            }
          }
        }}
      >
        <div className="form-grid">
          <div className="form-section-title">Payment</div>

          <Form.Item
            name="subscriptionId"
            hidden={isEdit}
            label="Subscription"
            rules={[{ required: true }]}
            className="form-grid__full"
          >
            <SearchSelect
              fetchKey="searchSubscriptions"
              endpoint="/subscription/search"
              labelKey="description"
              valueKey="subscriptionId"
              placeholder="Select subscription"
            />
          </Form.Item>

          <Form.Item name="paymentType" label="Payment Type" rules={[{ required: true }]}>
            <Select disabled={isEdit}>
              <Select.Option value="Regular">Regular</Select.Option>
              <Select.Option value="TopUp">Top-Up</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="status" label="Payment Status" rules={[{ required: true }]}>
            <Select options={PAYMENT_STATUS_OPTIONS} />
          </Form.Item>

          {paymentType === "TopUp" && !isEdit && (
            <Form.Item
              name="units"
              label="Packs"
              tooltip="Buy several packs in one payment instead of recording the same top-up repeatedly."
              rules={[{ required: true, type: "number", min: 1, max: 10 }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} max={10} />
            </Form.Item>
          )}

          {paymentType === "TopUp" && !isEdit && topUpPreview && (
            <Alert
              className="form-grid__full"
              type={topUpPreview.misconfigured ? "error" : "info"}
              showIcon
              message={
                topUpPreview.misconfigured
                  ? "This package has no top-up pack configured"
                  : `${topUpPreview.tokens.toLocaleString()} tokens of credit — ${topUpPreview.gross.toFixed(
                      2
                    )} ${currencyCode} incl. VAT`
              }
              description={
                topUpPreview.misconfigured
                  ? "Set the top-up token count and price on the package first. Selling a top-up without them would charge the full package price."
                  : "Credit is spent only after the plan's weekly and monthly allowance runs out, is not refilled at the next cycle reset, and expires with the current billing period. If the client is currently overdrawn, this purchase pays off that balance first."
              }
              style={{ marginBottom: 4 }}
            />
          )}

          <Form.Item label={paymentType === "TopUp" ? "Net Amount (packs × price)" : "Net Amount"}>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="amount" noStyle>
                <Input disabled style={{ width: "100%" }} />
              </Form.Item>
              <Input
                disabled
                value={currencyCode}
                style={{ width: 80, textAlign: "center" }}
              />
            </Space.Compact>
          </Form.Item>

          <Form.Item name="collectedAtUtc" label="Collected At">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <div className="form-section-title">Billing Period</div>

          <Form.Item name="fromDate" label="From Date">
            <DatePicker
              style={{ width: "100%" }}
              disabled={isEdit || paymentType === "TopUp"}
            />
          </Form.Item>

          <Form.Item name="toDate" label="To Date">
            <DatePicker
              style={{ width: "100%" }}
              disabled={
                isEdit ||
                paymentType === "TopUp" ||
                subscriptionMeta?.package?.periodType === "Annual"
              }
            />
          </Form.Item>

          <div className="form-section-title">References</div>

          <Form.Item name="externalProvider" label="External Provider">
            <Input placeholder="Stripe, bank transfer, cash" />
          </Form.Item>

          <Form.Item name="externalReference" label="External Reference">
            <Input placeholder="Gateway charge id or bank reference" />
          </Form.Item>

          <Form.Item
            name="failureReason"
            label="Failure Reason"
            hidden={currentStatus !== "Failed"}
            className="form-grid__full"
          >
            <Input placeholder="Reason for failed collection" />
          </Form.Item>

          <Form.Item name="notes" label="Notes" className="form-grid__full">
            <TextArea rows={3} placeholder="Internal notes or reconciliation context" />
          </Form.Item>
        </div>
      </Form>
    </FormPage>
  );
};

export default PaymentFormPage;
